import cloneDeep from 'lodash/cloneDeep';

import { surveyDatePromptIds } from '@api/surveys/utilities/constants';
import {
  sanitizeRichText,
  transformObjectToCamelCase,
  transformObjectToSnakeCase,
} from '@utilities';

import {
  FIELDS_USING_ID,
  instantSurveyGroupRefUrlVariable,
  MODEL_TYPES,
  PLACEHOLDER_IMAGE,
  PURCHASE_VOLUME_TYPES,
  QUESTION_GROUP_LOGIC,
  QUESTION_GROUP_LOGIC_BRANCHES,
  QUESTION_LOGIC_OPERATORS,
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_MAX_ANSWERS,
  QUESTION_TYPES_WITH_OPTIONS,
  QUESTION_TYPES_WITH_SUBQUESTIONS,
  SURVEY_HELP_URL,
} from './constants';

/**
 * @typedef {Object} InstantSurvey
 * @property {Object.<number, CarryForwardLogic>} carryForwardLogic
 * @property {string} country 'ca' | 'us'
 * @property {Group[]} groups
 * @property {number} [id]
 * @property {string} instantSurveyType
 * @property {?Date} issueDatetime
 * @property {SurveyQuestionLogic} questionLogic
 * @property {Question[]} questions
 * @property {string} [status]
 * @property {number} surveyRef
 * @property {?number} surveyTemplateId
 * @property {string} title
 * @property {boolean} [use_promo_credits]
 */

/**
 * @typedef {Object} CarryForwardLogic
 * @property {'SELECTED'|'UNSELECTED'} mode
 * @property {number} sourceSurveyQuestionId
 */

/**
 * @typedef {'CANCELED'|'ERROR'|'IN_REVIEW'|'NOT_ENOUGH_SAMPLE'|'NOT_VALIDATED'|'VALIDATED'|'VALIDATING'} GroupValidationStatus
 */

/**
 * @typedef {'PENDING'} PeopleGroupResolution
 */

/**
 * @typedef {Object} Group
 * @property {?string} genRepSampleType
 * @property {number|string} id
 * @property {string} name
 * @property {Object[]} promptAnswers
 * @property {?string} quotaGroupDefinitionRef
 * @property {?string} quotaGroupRef
 * @property {number} quotaLimit
 * @property {?PeopleGroupResolution} [peopleGroupResolution]
 * @property {GroupValidationStatus} validationStatus
 * @property {?number} validationUniqueUsers
 * @property {?number} validationUsers
 */

/**
 * @typedef {Object} Logic
 * @property {Object[]} answers
 * @property {LogicOperator} operator
 * @property {number} [question]
 *
 * @typedef {'ALL'|'ANY'|'NOT'} LogicOperator
 *
 * @typedef {Object} QuestionLogic
 * @property {Object} [disqualify]
 * @property {Logic[]} disqualify.logic
 * @property {LogicOperator} disqualify.operator
 * @property {Object} [skip]
 * @property {Logic[]} skip.logic
 * @property {LogicOperator} skip.operator
 *
 * @typedef {Object.<number, QuestionLogic>} SurveyQuestionLogic
 */

/**
 * formatInstantSurvey
 * takes an in-application InstantSurvey and formats it for create/update requests
 * @param {InstantSurvey} instantSurvey
 * @return {Object}
 */
export const formatInstantSurvey = ({
  carryForwardLogic,
  country,
  groups,
  id,
  instantSurveyType: instant_survey_type,
  languageInfo: language_info,
  launchMethod: launch_method,
  questionLogic: question_logic,
  questions,
  researchObjectives: research_objectives,
  status,
  surveyTemplateId: survey_template_id,
  title,
}) => {
  return {
    carry_forward_logic: transformObjectToSnakeCase(carryForwardLogic),
    country,
    groups: groups.map(formatInstantSurveyGroup),
    id,
    instant_survey_type,
    ...(language_info ? { language_info: transformObjectToSnakeCase(language_info) } : {}),
    launch_method,
    question_logic: formatQuestionLogic(question_logic),
    questions: questions.map(formatQuestion),
    research_objectives,
    status,
    survey_template_id,
    title,
  };
};

const transformTranslation = (translation) => {
  if (!translation) return translation;

  const { _delete, ...translationParams } = translation;
  return {
    ...transformObjectToCamelCase(translationParams),
    ...(isDefined(_delete) ? { _delete } : {}),
  };
};

const transformTranslations = (translations) => translations?.map(transformTranslation);

const formatTranslation = ({ _delete, id, languageCode, richText, text }) => ({
  _delete,
  id: typeof id === 'string' ? undefined : id,
  language_code: languageCode,
  rich_text: sanitizeRichText(richText ?? ''),
  text,
});

const formatTranslations = (translations) => translations?.map(formatTranslation);

/**
 * formatInstantSurveyGroup
 * prepares an in-app survey group to be sent to the instant survey service
 * @param {Group} group
 * @return {Object}
 */
const formatInstantSurveyGroup = ({
  _delete,
  conceptImage: concept_image,
  conceptName: concept_name,
  genRepSampleType,
  id,
  name,
  promptAnswers,
  quotaLimit: quota_limit,
  translations,
}) => {
  const normalizedId = id === null || typeof id === 'string' ? undefined : id;

  return {
    _delete,
    concept_image,
    concept_name,
    concept_name_translations: formatTranslations(translations),
    gen_rep_sample_type: genRepSampleType,
    id: normalizedId,
    prompt_answers: promptAnswers
      .filter(({ _delete, id }) => !_delete || (_delete && id)) // only delete previously saved answers
      .map(({ _delete, field, fieldType, id, operator, promptId, values }) => {
        let value;
        if (_delete) {
          value = '_delete';
        } else if (fieldType === 'DATE') {
          if (values.length === 1 && typeof values[0] === 'string') {
            value = values[0];
          } else if (values.length === 2 && typeof values[0] === 'string') {
            value = values.join(';');
          } else {
            value = values[0]?.date_range?.join(';');
          }
        } else if (
          fieldType === 'NUMERIC' &&
          Object.values(PURCHASE_VOLUME_TYPES).includes(field)
        ) {
          value = Array.isArray(values) ? values.join(';') : values;
        } else if (field === 'new_item') {
          value = values?.map((item) => item?.value || item?.id).join(';');
        } else {
          value = values
            ?.map(({ id, name }) => (FIELDS_USING_ID.includes(field) ? id : name))
            .join(';');
        }

        return {
          _delete,
          fe_notes: { insights_values: values },
          field,
          field_type: fieldType,
          id,
          operator,
          prompt_id: promptId,
          value,
        };
      }),
    quota_limit,
    title: name,
  };
};

/**
 * @typedef {Object} Question
 * @property {boolean} [aiGenerated]
 * @property {Object[]} answers
 * @property {string} [caption]
 * @property {number} [carriedFromOptionId]
 * @property {boolean} [excludeFromCarryForward]
 * @property {number} folder
 * @property {number} [id]
 * @property {Object} [image]
 * @property {string} [image.imageBucketName]
 * @property {string} [image.imageCdnUrl]
 * @property {string} [image.imageObjectKey]
 * @property {string} [image.imageObjectType]
 * @property {string[]} instantSurveyTypes
 * @property {boolean} [isCarryForwardGenerated]
 * @property {boolean} [isScreener]
 * @property {Object} properties
 * @property {boolean} [properties.forceCurrency]
 * @property {boolean} [properties.forceNumeric]
 * @property {boolean} [properties.forcePercent]
 * @property {number} [properties.maxAnswers]
 * @property {string} [properties.maxLength]
 * @property {number} [properties.maxNumber]
 * @property {number} [properties.maxTotal]
 * @property {string} [properties.minLength]
 * @property {number} [properties.minNumber]
 * @property {boolean} [properties.mustBeMax]
 * @property {boolean} [properties.onlyWholeNum]
 * @property {boolean} [properties.randomizeOptions]
 * @property {boolean} [properties.randomizeSubQuestions]
 * @property {number} [questionSourceId]
 * @property {Question[]} subQuestions
 * @property {string} text
 * @property {boolean} [turf]
 * @property {string} type
 * @property {boolean} [vw]
 */

const formatQuestionConcept = ({ _delete, id, image, name }) => ({
  _delete,
  id: typeof id === 'string' ? undefined : id,
  image: image ? { id: image.id } : null,
  name,
});

const formatGridSubQuestion = ({
  _delete,
  carriedFromOptionId,
  excludeFromCarryForward,
  id,
  image,
  isCarryForwardGenerated,
  richText,
  text,
  translations,
  type: question_type,
}) => ({
  _delete,
  carried_from_option_id: carriedFromOptionId,
  exclude_from_carry_forward: excludeFromCarryForward,
  id: typeof id === 'string' ? undefined : id,
  image: image ? { id: image.id } : null,
  is_carry_forward_generated: isCarryForwardGenerated,
  question_type,
  rich_text: sanitizeRichText(richText),
  text,
  translations: formatTranslations(translations),
});

/**
 * formatQuestion
 * @param {Question} question
 * @return {Object}
 */
export const formatQuestion = (question) => {
  const { questionClientId, ...questionWithoutClientId } = question;
  void questionClientId;
  const {
    aiGenerated: ai_generated,
    anchored,
    answers: options,
    concepts,
    excludeFromCarryForward: exclude_from_carry_forward,
    id,
    image,
    instantSurveyTypes: instant_survey_types,
    isCarryForwardGenerated: is_carry_forward_generated,
    properties,
    questionGroup,
    richText,
    showConceptImage,
    subQuestions,
    translations,
    type: question_type,
    ...rest
  } = questionWithoutClientId;

  const isTurf = question_type === QUESTION_TYPES.TURF.value;
  const isSequentialMonadic = question_type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value;
  const formattedSubQuestions = isSequentialMonadic
    ? subQuestions?.map(formatQuestion)
    : subQuestions?.map(formatGridSubQuestion);

  return {
    ai_generated,
    anchored,
    exclude_from_carry_forward,
    ...(typeof id !== 'string' && id !== null && id !== undefined ? { id } : {}),
    ...(isSequentialMonadic && { concepts: concepts?.map(formatQuestionConcept) || [] }),
    image: image ? { id: image.id } : null,
    instant_survey_types,
    is_carry_forward_generated,
    options: options?.map(
      ({
        _delete,
        carriedFromOptionId: carried_from_option_id,
        excludeFromCarryForward: exclude_from_carry_forward,
        id,
        image,
        isAnchored: anchored,
        isCarryForwardGenerated: is_carry_forward_generated,
        isCorrectAnswer: correct_answer,
        isExclusive: exclusive,
        isNoneOfTheAbove: none_of_the_above,
        isOther: other,
        properties, // some options can have { disqualify: true } for AskWhy disqualification
        richText,
        screenerValueMap,
        text,
        translations,
      }) => {
        return {
          _delete,
          anchored,
          carried_from_option_id,
          correct_answer,
          exclude_from_carry_forward,
          exclusive,
          id: typeof id === 'string' ? undefined : id,
          image: image ? { id: image.id } : null,
          is_carry_forward_generated,
          none_of_the_above,
          other,
          properties: transformObjectToSnakeCase(properties),
          rich_text: sanitizeRichText(richText),
          screener_value_map: screenerValueMap,
          text,
          translations: formatTranslations(translations),
        };
      }
    ),
    properties: {
      ...(properties?.isLanguageScreener && { is_language_screener: true }),
      ...(isDefined(properties?.isAttentionCheck) && {
        is_attention_check: properties.isAttentionCheck,
      }),
      ...(isDefined(properties?.categoryHierarchy) && {
        category_hierarchy: properties.categoryHierarchy,
      }),
      ...(question_type === QUESTION_TYPES.ESSAY.value && {
        max_length: properties?.maxLength,
        min_length: properties?.minLength,
      }),
      ...(QUESTION_TYPES_WITH_MAX_ANSWERS.includes(question_type) && {
        max_answers: properties?.maxAnswers,
        must_be_max: properties?.mustBeMax,
      }),
      ...(question_type === QUESTION_TYPES.CONTSUM.value && {
        force_currency: properties?.forceCurrency,
        force_numeric: properties?.forceNumeric,
        force_percent: properties?.forcePercent,
        max_number: properties?.maxNumber,
        max_total: properties?.maxTotal,
        min_number: properties?.minNumber,
        must_be_max: properties?.mustBeMax,
        only_whole_num: properties?.onlyWholeNum,
      }),
      ...(question_type === QUESTION_TYPES.MAX_DIFF.value && {
        best_label: properties?.bestLabel,
        best_label_translations: formatTranslations(properties?.bestLabelTranslations),
        number_of_sets: properties?.numberOfSets,
        options_per_set: properties?.optionsPerSet,
        worst_label: properties?.worstLabel,
        worst_label_translations: formatTranslations(properties?.worstLabelTranslations),
      }),
      ...(isDefined(properties?.productHierarchy) && {
        product_hierarchy: properties.productHierarchy,
      }),
      ...(isDefined(properties?.screenerPromptTypes) && {
        screener_prompt_types: properties.screenerPromptTypes,
      }),
      ...(question_type === QUESTION_TYPES.NUMERIC.value && {
        force_currency: properties?.forceCurrency,
        force_numeric: true, // INSRV-3041: Field must be true for Numeric question logic to be applied
        force_percent: properties?.forcePercent,
        max_number: properties?.maxNumber,
        min_number: properties?.minNumber,
        only_whole_num: properties?.onlyWholeNum,
      }),
      ...(question_type === QUESTION_TYPES.VAN_WESTENDORP.value && {
        display_order: properties?.displayOrder,
        force_currency: properties?.forceCurrency,
        force_numeric: properties?.forceNumeric,
        force_percent: properties?.forcePercent,
        max_number: properties?.maxNumber,
        min_number: properties?.minNumber,
        only_whole_num: properties?.onlyWholeNum,
      }),
      ...(QUESTION_TYPES_WITH_OPTIONS.includes(question_type) && {
        randomize_options: properties?.randomizeOptions,
      }),
      ...(QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question_type) && {
        randomize_subquestions: properties?.randomizeSubQuestions ?? false,
      }),
      ...(isDefined(properties?.storeHierarchy) && {
        store_hierarchy: properties.storeHierarchy,
      }),
    },
    question_group: questionGroup !== undefined ? questionGroup : undefined,
    question_type: isTurf ? QUESTION_TYPES.CHECKBOX.value : question_type,
    randomize_options: false, // INSRV-2367
    rich_text: sanitizeRichText(richText),
    show_concept_image: showConceptImage,
    sub_questions: formattedSubQuestions,
    translations: formatTranslations(translations),
    turf: isTurf,
    ...transformObjectToSnakeCase(rest),
  };
};

/**
 * formatQuestionLogic
 * @param {Object} questionLogic
 * @return {Object}
 */
/**
 * formatQuestionLogicRow
 * Flattens a single skip/option-style logic row for the API: operators become their
 * `.value`, hydrated `question` objects become ids, and answer objects become ids.
 * @param {Object} row
 * @return {Object}
 */
const formatQuestionLogicRow = (row) => {
  if (row.url_variable === instantSurveyGroupRefUrlVariable) {
    return {
      operator: row.operator.value,
      url_variable: instantSurveyGroupRefUrlVariable,
      values: row.values,
    };
  } else if (Object.prototype.hasOwnProperty.call(row, 'comparison_value')) {
    return {
      comparison_value: row.comparison_value,
      operator: row.operator.value,
      question: row.question?.id,
    };
  }
  return {
    answers: row.answers.map((answer) => answer.id),
    operator: row.operator.value,
    question: row.question?.id,
  };
};

export const formatQuestionLogic = (questionLogic) => {
  const modifiedQuestionLogic = cloneDeep(questionLogic);
  for (const questionId in questionLogic) {
    // Question group logic is keyed by group id (not question id) and lives under a
    // reserved key. Flatten each branch of each group, mirroring per-question skip logic —
    // group disqualify rows are skip-shaped (they reference an external trigger question),
    // unlike per-question disqualify rows.
    if (questionId === QUESTION_GROUP_LOGIC) {
      const groupLogic = modifiedQuestionLogic[questionId];
      for (const groupId in groupLogic) {
        const group = groupLogic[groupId];
        QUESTION_GROUP_LOGIC_BRANCHES.forEach((branch) => {
          const branchLogic = group?.[branch];
          if (!branchLogic) return;
          // A branch left with no rows would be persisted as an empty rule; drop it instead.
          if (!branchLogic.logic?.length) {
            delete group[branch];
            return;
          }
          branchLogic.operator = branchLogic.operator.value;
          branchLogic.logic = branchLogic.logic.map(formatQuestionLogicRow);
        });
        if (!group || Object.keys(group).length === 0) {
          delete groupLogic[groupId];
        }
      }
      if (Object.keys(groupLogic).length === 0) {
        delete modifiedQuestionLogic[questionId];
      }
      continue;
    }

    const question = modifiedQuestionLogic[questionId];

    if (Object.keys(question).length > 0) {
      if (question.disqualify) {
        if (Array.isArray(question.disqualify.logic)) {
          question.disqualify.operator = question.disqualify.operator.value;
          question.disqualify.logic = question.disqualify.logic.map((row) => {
            if (row.url_variable === instantSurveyGroupRefUrlVariable) {
              return {
                operator: row.operator.value,
                url_variable: instantSurveyGroupRefUrlVariable,
                values: row.values,
              };
            }
            return {
              answers: row.answers.map((answer) => answer.id),
              operator: row.operator.value,
            };
          });
        } else {
          question.disqualify.answers = question.disqualify.answers.map((answer) => answer.id);
          question.disqualify.operator = question.disqualify.operator.value;
        }

        delete question.disqualify.hasAnswerError;
        delete question.disqualify.isFirstAnswerError;
      }

      if (question.skip) {
        question.skip.operator = question.skip.operator.value;
        question.skip.logic = question.skip.logic.map((row) => {
          if (row.url_variable === instantSurveyGroupRefUrlVariable) {
            return {
              operator: row.operator.value,
              url_variable: instantSurveyGroupRefUrlVariable,
              values: row.values,
            };
          } else if (Object.prototype.hasOwnProperty.call(row, 'comparison_value')) {
            return {
              comparison_value: row.comparison_value,
              operator: row.operator.value,
              question: row.question?.id,
            };
          } else {
            return {
              answers: row.answers.map((answer) => answer.id),
              operator: row.operator.value,
              question: row.question?.id,
            };
          }
        });
      }

      if (question.option) {
        for (const answerId in question.option) {
          question.option[answerId].operator = question.option[answerId].operator.value;
          question.option[answerId].logic = question.option[answerId].logic.map((row) => {
            if (row.url_variable === instantSurveyGroupRefUrlVariable) {
              return {
                operator: row.operator.value,
                url_variable: instantSurveyGroupRefUrlVariable,
                values: row.values,
              };
            } else if (Object.hasOwn(row, 'comparison_value')) {
              return {
                comparison_value: row.comparison_value,
                operator: row.operator.value,
                question: row.question?.id,
              };
            } else {
              return {
                answers: row.answers.map((answer) => answer.id),
                operator: row.operator.value,
                question: row.question?.id,
              };
            }
          });
        }
      }
    } else {
      delete modifiedQuestionLogic[questionId];
    }
  }
  return modifiedQuestionLogic;
};

/**
 * isDefined
 * @param {*} value
 * @return {boolean}
 */
const isDefined = (value) => typeof value !== 'undefined';

/**
 * transformAskWhyQuestions
 * @param {Object[]} response
 * @param {Object[]} response.results
 * @return {Question[]}
 */
export const transformAskWhyQuestions = ({ results }) => results.map(transformQuestion);

/**
 * @typedef {Object} ExternalQuestion
 * @property {number} id
 * @property {Object[]} options
 * @property {number} options.id
 * @property {string} options.title
 * @property {string} options.value
 * @property {Object[]} [subQuestions]
 * @property {string} subQuestions.id
 * @property {string} subQuestions.text
 * @property {string} subQuestions.title
 * @property {string} title
 * @property {string} type
 */

/**
 * transformExternalQuestion
 * @param {Object} question
 * @param {boolean?} question.comment
 * @param {string[]} question.description empty in example referenced
 * @param {boolean?} question.has_showhide_deps
 * @param {number} question.id
 * @param {Object[]} question.options
 * @param {number} question.options.id
 * @param {string} question.options.title
 * @param {string} question.options.value appears to be the same as title
 * @param {string} [question.pipe_values_from]
 * @param {Object?} question.properties eg. { hidden, labels_right, map_key, option_sort, orientation, required }
 * @param {string} question.shortname deprecated
 * @param {Object[]} [question.sub_questions]
 * @param {number} question.sub_questions.id
 * @param {string} question.sub_questions.title
 * @param {string} question.text
 * @param {string} question.title
 * @param {boolean?} question.turf
 * @param {string} question.type
 * @param {Array|Object} question.varname
 * @return {ExternalQuestion}
 */
export const transformExternalQuestion = ({
  id,
  options,
  rich_text,
  sub_questions,
  text,
  title,
  translations,
  turf,
  type,
}) => ({
  id,
  options: options.map(({ id, title, translations, value }) => ({
    id,
    title,
    // Per-language translations carried through for the reports-page language switcher. Kept in the
    // backend shape ([{ language_code, text, rich_text }]); resolved to display text in
    // applyQuestionTranslations when a language is applied.
    translations,
    value,
  })),
  // Only populate richText when the question carries translations — applyQuestionTranslations
  // overwrites it per language. For untranslated surveys richText stays undefined so consumers
  // (Results tab, PPTX, DOCX) fall through to `title` as before, avoiding any rendering change.
  ...(translations?.length ? { richText: rich_text } : {}),
  subQuestions: sub_questions?.map(({ id, text, title, translations }) => ({
    id,
    text,
    title: title || text,
    translations,
  })),
  text,
  title,
  translations,
  turf,
  type,
});

/**
 * transformExternalQuestions
 * @param {Object[]} questions
 * @return {ExternalQuestion[]}
 */
export const transformExternalQuestions = (questions) => questions.map(transformExternalQuestion);

/**
 * transformSurveyPrompt
 * @param {Object} prompt
 * @param {boolean} prompt.advanced
 * @param {boolean} [prompt.allow_custom_date]
 * @param {string} prompt.display_name
 * @param {Date} [prompt.ecomm_delay_date]
 * @param {number} [prompt.ecomm_lag_period_in_days]
 * @param {string} prompt.empty_value
 * @param {Date} [prompt.general_lag_date]
 * @param {number} prompt.id
 * @param {boolean} prompt.in_modal
 * @param {string} prompt.insights_key
 * @param {string} prompt.instant_survey_type
 * @param {boolean} prompt.is_prefix_enabled
 * @param {number} [prompt.lag_period_in_days]
 * @param {number} [prompt.max_values]
 * @param {number} [prompt.min_answers]
 * @param {string} prompt.prompt_key
 * @param {Object} prompt.prompt_type
 * @param {boolean} prompt.required
 * @param {boolean} prompt.required_for_modal
 * @param {boolean} prompt.search_enabled
 * @param {boolean} prompt.sort_enabled
 * @return {Object}
 */
export const transformSurveyPrompt = ({
  advanced: isAdvanced,
  allow_custom_date: hasCustomDateOption,
  behavior_group: behaviorGroup,
  display_name: name,
  empty_value: emptyValue,
  general_lag_date: maxDate,
  id,
  in_modal: isModalPrompt,
  insights_key: insightsKey,
  // instant_survey_type,
  is_prefix_enabled: isPrefixIncluded,
  max_values: maxValues,
  min_answers: minAnswers,
  prompt_key: promptKey,
  prompt_type: promptType,
  required: isRequired,
  required_for_modal: isRequiredForModal,
  search_enabled: isSearchEnabled,
  sort_enabled: isSortEnabled,
}) => {
  const isDateRange = surveyDatePromptIds.includes(insightsKey);
  const isComparisonPeriod = insightsKey === 'comparison_period';
  return {
    ...(promptType.fields?.length && {
      attributes: promptType.fields.map(
        ({
          description,
          field,
          insights_id: id,
          is_custom: isCustom,
          is_fixed: isFixed,
          is_placeholder: isPlaceholder,
          name,
        }) => ({
          description,
          field,
          id,
          isCustom,
          isFixed,
          isPlaceholder,
          name,
        })
      ),
    }),
    ...(isComparisonPeriod
      ? {
          dateOptions: promptType.options?.map((option) => ({
            options: [
              {
                isDefault: option.is_default,
                label: option.display,
                value: [option.key],
              },
            ],
            timeFrame: option.display,
          })),
        }
      : {}),
    ...(isDateRange
      ? {
          dateOptions: promptType.options?.map(
            ({ in_modal: isInModal, options = [], time_frame: timeFrame, tooltip }) => ({
              isInModal,
              options: options.map(({ date_range, label }) => {
                return {
                  label,
                  value: date_range,
                };
              }),
              timeFrame,
              type: tooltip,
            })
          ),
        }
      : {}),
    behaviorGroup,
    emptyValue,
    hasCustomDateOption,
    id,
    insightsKey,
    isAdvanced,
    isModalPrompt,
    isPrefixIncluded,
    isRequired,
    isRequiredForModal,
    isSearchEnabled,
    isSortEnabled,
    maxDate,
    maxValues: isDateRange ? 1 : maxValues,
    minAnswers,
    name,
    ...(!isDateRange && !isComparisonPeriod && promptType.options?.length > 0
      ? {
          objects: promptType.options.map(({ display: name, key: id }) => ({
            field: promptType?.option_field,
            id,
            name,
          })),
        }
      : {}),
    promptKey,
  };
};

/**
 * transformQuestionnaireJob
 * @param {Object} response
 * @return {Object}
 */
export const transformQuestionnaireJobResponse = (response) => {
  const questions = response.output?.questions.map((question, index) => ({
    aiGenerated: true,
    answers: question.options
      ? question.options.map((opt, i) => ({
          id: `opt-${index}-${i}`,
          text: opt.text,
          ...opt,
        }))
      : [],
    id: `question-${index}`,
    subQuestions: question.sub_questions || [],
    text: question.name,
    type: question.question_type,
  }));

  return {
    output: {
      questions,
    },
    status: response.status,
  };
};

/**
 * transformSurveyQuestionResponseSummaryResponses
 * @param {Object} response
 * @param {Object[]} response.results
 * @return {Object}
 */
export const transformSurveyQuestionResponseSummaryResponses = (response) => {
  const questionSummariesMap = {};
  let wholeSurveySummary = null;

  response.results.forEach(
    ({
      active,
      additional_params: additionalParams,
      admin_note: adminNote,
      created,
      id,
      insights_job_ref: insightsJobRef,
      model_used: modelUsed,
      output,
      parent_job: parentJob,
      prompts,
      report_type: reportType,
      status,
      status_message: statusMessage,
      updated,
      user_left_feedback: userLeftFeedback,
      user_left_negative_feedback: userLeftNegativeFeedback,
    }) => {
      const formattedSummary = {
        active,
        additionalParams: {
          compareBy: additionalParams?.compare_by,
          compareByName: additionalParams?.compare_by_name,
          compareByValue: additionalParams?.compare_by_value,
          insightsSurveyId: additionalParams?.insights_survey_id,
          questionId: additionalParams?.question_id,
          questionSourceId: additionalParams?.question_source_id,
          region: additionalParams?.region,
          surveyId: additionalParams?.survey_id,
        },
        adminNote,
        created,
        id,
        insightsJobRef,
        modelUsed,
        output,
        parentJob,
        prompts,
        reportType,
        status,
        statusMessage,
        updated,
        userLeftFeedback,
        userLeftNegativeFeedback,
      };

      if (reportType === 'WHOLE_SURVEY' || reportType === 'VIDEO_TRANSCRIPT_SUMMARY') {
        wholeSurveySummary = formattedSummary;
        return;
      }

      const questionSourceId = additionalParams?.question_source_id;
      if (!questionSummariesMap[questionSourceId]) {
        questionSummariesMap[questionSourceId] = [formattedSummary];
      } else {
        questionSummariesMap[questionSourceId].push(formattedSummary);
      }
    }
  );

  return {
    questionSummaries: questionSummariesMap,
    wholeSurveySummary,
  };
};

/**
 * transformImageObject
 * @param {Object} image
 * @param {string} image.bucket_name
 * @param {string} image.cdn_url
 * @param {number} image.id
 * @param {string} image.object_key
 * @param {string} image.object_type
 * @param {Object} [image.fields]
 * @param {string} image.url
 * @return {Object}
 */
export const transformImageObject = ({
  bucket_name: bucketName,
  cdn_url: cdnUrl,
  fields,
  id,
  object_key: objectKey,
  object_type: objectType,
  url,
}) => ({
  bucketName,
  cdnUrl: cdnUrl.indexOf('http://localhost') !== -1 ? PLACEHOLDER_IMAGE : cdnUrl,
  fields,
  id,
  objectKey,
  objectType,
  url,
});

const transformQuestionConcept = ({ id, image, name }) => ({
  id,
  image: image ? transformImageObject(image) : null,
  name,
});

/**
 * transformInstantSurvey
 * @param {Object} instantSurvey
 * @param {Object} instantSurvey.ask_why_value_map
 * @param {?Date} instantSurvey.build_quota_groups_datetime
 * @param {?Object} instantSurvey.carry_forward_logic
 * @param {?string} instantSurvey.contact_list_ref
 * @param {string} instantSurvey.country
 * @param {Date} instantSurvey.created_at
 * @param {?Date} instantSurvey.expire_datetime
 * @param {Object[]} instantSurvey.groups
 * @param {number} instantSurvey.id
 * @param {?number} instantSurvey.insights_survey_ref
 * @param {string} instantSurvey.instant_survey_type
 * @param {?Date} instantSurvey.issue_datetime
 * @param {?number} instantSurvey.job_ref
 * @param {Object} instantSurvey.question_logic
 * @param {Object[]} instantSurvey.questions
 * @param {?number} [instantSurvey.response_count] included when query has include_response_count=true
 * @param {string} instantSurvey.status
 * @param {?Date} instantSurvey.survey_created_datetime
 * @param {?string|number} instantSurvey.survey_ref
 * @param {?string} instantSurvey.survey_source
 * @param {?number} instantSurvey.survey_source_id
 * @param {?string} instantSurvey.survey_source_override
 * @param {?number} instantSurvey.survey_template_id
 * @param {string} instantSurvey.title
 * @param {string} instantSurvey.research_objectives
 * @param {Date} instantSurvey.updated_at
 * @param {?number} instantSurvey.validation_version
 * @return {InstantSurvey}
 */

export const transformInstantSurvey = (
  {
    groups,
    question_groups: questionGroupsMap,
    question_logic: questionLogic,
    questions,
    survey_ref: surveyRef,
    ...surveyParams
  },
  { isMultiLevelDisqualifyEnabled = true } = {}
) => {
  const transformedSurvey = transformObjectToCamelCase(surveyParams);
  const transformedQuestions = questions.map(transformQuestion);
  return {
    ...transformedSurvey,
    groups: groups.map(transformInstantSurveyGroup),
    questionGroups: transformObjectToCamelCase(questionGroupsMap ?? {}),
    questions: transformedQuestions,
    ...transformQuestionLogic({
      isMultiLevelDisqualifyEnabled,
      questionLogic,
      questions: transformedQuestions,
    }),
    surveyRef: Number(surveyRef),
  };
};

/**
 * transformInstantSurveyGroup
 * @param {Object} group
 * @param {?string} group.contact_list_group_ref
 * @param {Date} group.created_at
 * @param {?string} group.gen_rep_sample_type
 * @param {number} group.id
 * @param {Array} group.prompt_answers
 * @param {?string} group.quota_group_definition_ref
 * @param {?string} group.quota_group_ref
 * @param {number} group.quota_limit
 * @param {?PeopleGroupResolution} [group.people_group_resolution]
 * @param {?number} [group.response_count] included when query has include_response_count=true
 * @param {string} group.title
 * @param {Date} group.updated_at
 * @param {?Date} group.validation_datetime
 * @param {string} group.validation_status
 * @param {?number} group.validation_unique_users
 * @param {?number} group.validation_users
 * @param {number} group.validation_version
 * @return {Group}
 */
const transformInstantSurveyGroup = ({
  concept_image,
  concept_name: conceptName,
  concept_name_translations: translations,
  continuously_fed: continuouslyFed,
  gen_rep_sample_type: genRepSampleType,
  id,
  launch_method: launchMethod,
  people_group_resolution: peopleGroupResolution,
  prompt_answers: promptAnswers,
  quota_group_definition_ref: quotaGroupDefinitionRef,
  quota_group_ref: quotaGroupRef,
  quota_limit: quotaLimit,
  response_count: responseCount,
  title: name,
  validation_datetime: validationDatetime,
  validation_status: validationStatus,
  validation_unique_users: validationUniqueUsers,
  validation_users: validationUsers,
}) => ({
  conceptImage: concept_image ? transformImageObject(concept_image) : null,
  conceptName,
  continuouslyFed,
  genRepSampleType,
  id,
  launchMethod,
  name,
  peopleGroupResolution,
  promptAnswers: promptAnswers.map(
    ({ fe_notes: feNotes, field, field_type: fieldType, id, operator, prompt_id: promptId }) => ({
      field,
      fieldType,
      id,
      operator,
      promptId,
      values: feNotes?.insights_values,
    })
  ),
  quotaGroupDefinitionRef,
  quotaGroupRef,
  quotaLimit,
  responseCount,
  translations: transformTranslations(translations),
  validationDatetime,
  validationStatus,
  validationUniqueUsers,
  validationUsers,
});

/**
 * transformSurveyQuotaGroups
 * @param {Object} survey
 * @param {Object[]} survey.quotagroup_definitions
 * @return {Object[]}
 */
export const transformSurveyQuotaGroups = ({
  quotagroup_definitions: quotaGroupDefinitions = [],
}) =>
  quotaGroupDefinitions.map(({ id, quota_limit: quotaLimit }) => ({
    id,
    quotaLimit,
  }));

/**
 * @typedef {Object} InstantSurveyType
 * @property {Object[]} advancedPrompts
 * @property {string} description
 * @property {string} helpUrl
 * @property {string[][]} hierarchy_search_prompts
 * @property {number} id
 * @property {string} label
 * @property {string} madlib
 * @property {string} name
 * @property {Object[]} prompts
 */

/**
 * transformInstantSurveyType
 * @param {Object} instantSurveyType
 * @param {boolean} instantSurveyType.deprecated
 * @param {string} instantSurveyType.description
 * @param {string} [instantSurveyType.help_url]
 * @param {string[][]} instantSurveyType.hierarchy_search_prompts eg: [ ['categories', 'product'] ]
 * @param {string} instantSurveyType.id
 * @param {string} instantSurveyType.label
 * @param {Object} instantSurveyType.modal_prompt_requirements
 * @param {string} instantSurveyType.name
 * @param {string[]} instantSurveyType.panels
 * @param {number} instantSurveyType.priority
 * @param {Object[]} instantSurveyType.prompts
 * @param {string} instantSurveyType.prompts_madlib
 * @param {Object[]} instantSurveyType.screener_questions
 * @param {boolean} instantSurveyType.survey_templates_available
 * @return {InstantSurveyType}
 */
export const transformInstantSurveyType = ({
  description,
  help_url: helpUrl = SURVEY_HELP_URL,
  hierarchy_search_prompts,
  id,
  is_specialty,
  label,
  modal_prompt_requirements: modalPromptRequirements,
  name,
  prompts,
  prompts_madlib: madlib,
  screener_questions,
}) => {
  return {
    advancedPrompts: prompts
      .filter(({ advanced, prompt_key }) => advanced && prompt_key !== 'images') // images is still TBD
      .map(transformSurveyPrompt),
    description,
    helpUrl,
    hierarchy_search_prompts,
    id,
    isSpecialty: is_specialty,
    label,
    madlib,
    modalPromptRequirements,
    name,
    prompts: prompts
      .filter(({ advanced, prompt_key }) => !advanced && prompt_key !== 'images')
      .map(transformSurveyPrompt),
    screenerQuestions: screener_questions?.map(transformQuestion) || [],
  };
};

/**
 * transformInstantSurveyTypes
 * @param {Object} response
 * @param {Array} response.results
 * @return {Object}
 */
export const transformInstantSurveyTypes = ({ results, ...rest }) => {
  return {
    results: results.map((surveyType) => transformInstantSurveyType(surveyType)),
    ...rest,
  };
};

export const flattenSequentialMonadicQuestions = (questions = []) =>
  (questions || []).flatMap((question) => {
    if (question?.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) return [question];

    return (question.subQuestions || []).map((subQuestion) => ({
      ...subQuestion,
      concepts: question.concepts || [],
      isSequentialMonadicSubQuestion: true,
      sequentialMonadicQuestionId: question.id,
    }));
  });

export const isSequentialMonadicMultiSelectQuestion = (question = {}) =>
  question?.type === QUESTION_TYPES.CHECKBOX.value &&
  question?.isSequentialMonadicSubQuestion === true;

/**
 * parseQuestionGroupId
 * Extracts the leaf-level group id from a question_group_path string (e.g. "55/60" → 60).
 * @param {string|null|undefined} questionGroupPath
 * @return {number|null}
 */
const parseQuestionGroupId = (questionGroupPath) => {
  if (!questionGroupPath) return null;
  const parts = String(questionGroupPath).split('/');
  const leafId = Number(parts[parts.length - 1]);
  return isNaN(leafId) ? null : leafId;
};

/**
 * transformQuestion
 * @param {Object} question
 * @param {boolean} question.ai_generated
 * @param {number[]} [question.bottom_n_options]
 * @param {string} question.caption
 * @param {number} [question.carried_from_option_id]
 * @param {Date} question.created_at
 * @param {boolean} [question.deleted]
 * @param {boolean} [question.exclude_from_carry_forward]
 * @param {number} question.folder
 * @param {number} question.id
 * @param {Object} [question.image]
 * @param {number} [question.insights_question_ref]
 * @param {string[]} question.instant_survey_types
 * @param {boolean} [question.is_carry_forward_generated]
 * @param {boolean} [question.is_screener]
 * @param {string} question.model_type
 * @param {Object[]} question.options
 * @param {Object} [question.parameters]
 * @param {Object} question.properties
 * @param {string} [question.properties.best_label]
 * @param {Object[]} [question.properties.best_label_translations]
 * @param {boolean} [question.properties.force_currency]
 * @param {boolean} [question.properties.force_numeric]
 * @param {boolean} [question.properties.force_percent]
 * @param {number} [question.properties.max_answers]
 * @param {number} [question.properties.max_number]
 * @param {number} [question.properties.max_total]
 * @param {number} [question.properties.min_number]
 * @param {boolean} [question.properties.must_be_max]
 * @param {number} [question.properties.number_of_sets]
 * @param {boolean} [question.properties.only_whole_num]
 * @param {number} [question.properties.options_per_set]
 * @param {boolean} [question.properties.randomize_options]
 * @param {string} [question.properties.worst_label]
 * @param {Object[]} [question.properties.worst_label_translations]
 * @param {string} [question.question_source_id] question id from the survey source after publish
 * @param {string} question.question_type
 * @param {boolean} question.randomize_options deprecated as part of INSRV-2367
 * @param {boolean} question.show_concept_image
 * @param {Object[]} question.sub_questions
 * @param {boolean} question.survey_question
 * @param {string} question.text
 * @param {string} [question.title]
 * @param {number[]} [question.top_n_options]
 * @param {Object[]} [question.translations]
 * @param {boolean} question.turf
 * @param {string} question.type
 * @param {Date} question.updated_at
 * @param {boolean} [question.vw]
 * @return {Question}
 */
export const transformQuestion = ({
  ai_generated: aiGenerated,
  anchored,
  bottom_n_options: bottomNOptions,
  caption,
  carried_from_option_id: carriedFromOptionId,
  concepts,
  exclude_from_carry_forward: excludeFromCarryForward,
  folder,
  id,
  image,
  instant_survey_types: instantSurveyTypes,
  is_carry_forward_generated: isCarryForwardGenerated,
  is_screener: isScreener,
  options,
  properties,
  question_group_path: questionGroupPath,
  question_source_id: questionSourceId,
  question_type: type,
  randomize_options: randomizeOptions,
  rich_text: richText,
  screener_label: screenerLabel,
  screener_value_map: screenerValueMap,
  show_concept_image: showConceptImage,
  sub_questions: subQuestions,
  text,
  top_n_options: topNOptions,
  translations,
  turf,
  vw,
}) => ({
  aiGenerated,
  anchored,
  answers: options.map(
    ({
      anchored: isAnchored,
      carried_from_option_id: carriedFromOptionId,
      correct_answer: isCorrectAnswer,
      exclude_from_carry_forward: excludeFromCarryForward,
      exclusive: isExclusive,
      id,
      image,
      is_carry_forward_generated: isCarryForwardGenerated,
      none_of_the_above: isNoneOfTheAbove,
      option_source_id: optionSourceId,
      other: isOther,
      properties, // some options can have { disqualify: true } for AskWhy disqualification
      rich_text: richText,
      screener_value_map: screenerValueMap,
      text,
      translations,
    }) => ({
      carriedFromOptionId,
      excludeFromCarryForward,
      id,
      image: image ? transformImageObject(image) : null,
      isAnchored,
      isCarryForwardGenerated,
      isCorrectAnswer,
      isExclusive,
      isNoneOfTheAbove,
      isOther,
      optionSourceId:
        optionSourceId !== null && optionSourceId !== undefined
          ? Number(optionSourceId)
          : optionSourceId,
      properties: transformObjectToCamelCase(properties),
      richText: sanitizeRichText(richText),
      screenerValueMap,
      text,
      title: text,
      translations: transformTranslations(translations),
      value: text,
    })
  ),
  bottomNOptions,
  caption,
  carriedFromOptionId,
  concepts: concepts?.map(transformQuestionConcept) || [],
  excludeFromCarryForward,
  folder,
  id,
  image: image ? transformImageObject(image) : null,
  instantSurveyTypes,
  isCarryForwardGenerated,
  isScreener,
  properties: {
    ...(isDefined(properties?.best_label) && { bestLabel: properties.best_label }),
    ...(isDefined(properties?.best_label_translations) && {
      bestLabelTranslations: transformTranslations(properties.best_label_translations),
    }),
    ...(isDefined(properties?.category_hierarchy) && {
      categoryHierarchy: properties.category_hierarchy,
    }),
    ...(isDefined(properties?.display_order) && { displayOrder: properties.display_order }),
    ...(isDefined(properties?.disqualify) && { disqualify: properties.disqualify }),
    ...(isDefined(properties?.force_currency) && {
      forceCurrency: properties.force_currency,
    }),
    ...(isDefined(properties?.force_numeric) && {
      forceNumeric:
        // INSRV-3041: Numeric questions will always be force_numeric on the BE, FE only wants one
        // of these options (currency, numeric, percent) to be true
        type === QUESTION_TYPES.NUMERIC.value
          ? !properties?.force_currency && !properties?.force_percent
          : properties.force_numeric,
    }),
    ...(isDefined(properties?.force_percent) && {
      forcePercent: properties.force_percent,
    }),
    ...(isDefined(properties?.is_language_screener) && {
      isLanguageScreener: properties.is_language_screener,
    }),
    ...(isDefined(properties?.is_attention_check) && {
      isAttentionCheck: properties.is_attention_check,
    }),
    ...(isDefined(properties?.max_answers) && { maxAnswers: properties.max_answers }),
    ...(isDefined(properties?.max_length) && { maxLength: properties.max_length }),
    ...(isDefined(properties?.max_number) && { maxNumber: properties.max_number }),
    ...(isDefined(properties?.max_total) && { maxTotal: properties.max_total }),
    ...(isDefined(properties?.min_length) && { minLength: properties.min_length }),
    ...(isDefined(properties?.min_number) && { minNumber: properties.min_number }),
    ...(isDefined(properties?.must_be_max) && { mustBeMax: properties.must_be_max }),
    ...(isDefined(properties?.number_of_sets) && {
      numberOfSets: properties.number_of_sets,
    }),
    ...(isDefined(properties?.only_whole_num) && {
      onlyWholeNum: properties.only_whole_num,
    }),
    ...(isDefined(properties?.options_per_set) && {
      optionsPerSet: properties.options_per_set,
    }),
    ...(isDefined(properties?.product_hierarchy) && {
      productHierarchy: properties.product_hierarchy,
    }),
    ...(isDefined(properties?.screener_prompt_types) && {
      screenerPromptTypes: properties.screener_prompt_types,
    }),
    // preserve randomizedOptions true when reading existing questions
    randomizeOptions: properties?.randomize_options || randomizeOptions,
    ...(isDefined(properties?.worst_label) && { worstLabel: properties.worst_label }),
    ...(isDefined(properties?.worst_label_translations) && {
      worstLabelTranslations: transformTranslations(properties.worst_label_translations),
    }),
    randomizeSubQuestions: !!properties?.randomize_subquestions,
    ...(isDefined(properties?.store_hierarchy) && {
      storeHierarchy: properties.store_hierarchy,
    }),
  },
  questionGroup: parseQuestionGroupId(questionGroupPath),
  questionSourceId: questionSourceId ? Number(questionSourceId) : questionSourceId,
  richText: sanitizeRichText(richText),
  screenerLabel,
  screenerValueMap,
  showConceptImage,
  subQuestions: subQuestions.map(transformQuestion),
  text,
  topNOptions,
  translations: transformTranslations(translations),
  turf,
  type: turf ? QUESTION_TYPES.TURF.value : type,
  vw,
});

/**
 * transformQuestionFolder
 * @param {Object} folder
 * @param {Object[]} folder.contents
 * @param {number} folder.count
 * @param {Object} folder.current_questionfolder
 * @param {number} folder.limit
 * @param {number} folder.offset
 * @param {Object} [folder.parent_folder]
 * @return {Object}
 */
export const transformQuestionFolder = ({ contents, count, limit, offset }) => ({
  contents: contents.map((content) => {
    switch (content.type) {
      case MODEL_TYPES.QUESTION:
        return transformQuestion(content);
      case MODEL_TYPES.QUESTION_FOLDER:
        // `isFolder` still necessary while using existing ModalQuestionsLibrary/components/Results
        return { ...content, isFolder: true, text: content.title };
      default:
        return content;
    }
  }),
  count,
  limit,
  offset,
});

/**
 * transformSurveyTemplate
 * @param {Object} surveyTemplate
 * @param {Object[]} [surveyTemplate.questions]
 * @return {Object}
 */
export const transformSurveyTemplate = (surveyTemplate) => ({
  ...surveyTemplate,
  ...(Array.isArray(surveyTemplate.questions) && {
    questions: surveyTemplate.questions.map(transformQuestion),
  }),
});

/**
 * transformSurveyTemplates
 * @param {Object} templates
 * @param {number} templates.count
 * @param {Object[]} templates.results
 * @return {Object}
 */
export const transformSurveyTemplates = ({ count, results }) => ({
  count,
  results: (results || []).map(transformSurveyTemplate),
});

const getQuestionLogicQuestions = (questions = []) =>
  questions.flatMap((question) =>
    question.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value
      ? [question, ...(question.subQuestions || [])]
      : [question]
  );

const findQuestionLogicQuestionById = (questionLogicQuestions = [], questionId) =>
  questionLogicQuestions.find(({ id }) => String(id) === String(questionId));

/**
 * transformQuestionLogic
 * @param {Object} options.questionLogic
 * @param {Question[]} options.questions
 * @return {Object}
 */
export const transformQuestionLogic = ({
  isMultiLevelDisqualifyEnabled = true,
  questionLogic,
  questions,
}) => {
  let hasQuestionLogicError = false;
  const modifiedQuestionLogic = cloneDeep(questionLogic);
  const questionLogicQuestions = getQuestionLogicQuestions(questions);

  /**
   * hydrateSkipLogicRow
   * Hydrates a single skip/option-style logic row from the API shape (operator values, question
   * ids, answer ids) into the FE shape (operator objects, hydrated Question objects, hydrated
   * answer objects). Flips `hasQuestionLogicError` on invalid rows.
   * @param {Object} row
   * @return {Object}
   */
  const hydrateSkipLogicRow = (row) => {
    if (row.url_variable === instantSurveyGroupRefUrlVariable) {
      if (row.values.length === 0) {
        hasQuestionLogicError = true;
      }
      return {
        operator: QUESTION_LOGIC_OPERATORS[row.operator],
        url_variable: instantSurveyGroupRefUrlVariable,
        values: row.values,
      };
    } else if (Object.prototype.hasOwnProperty.call(row, 'comparison_value')) {
      const skipQuestion = findQuestionLogicQuestionById(questionLogicQuestions, row.question);
      if (!skipQuestion || row.comparison_value == null || row.comparison_value === '') {
        hasQuestionLogicError = true;
      }

      return {
        comparison_value: row.comparison_value,
        operator: QUESTION_LOGIC_OPERATORS[row.operator],
        question: skipQuestion,
      };
    }
    const skipQuestion = findQuestionLogicQuestionById(questionLogicQuestions, row.question);
    const initialAnswersLength = row.answers?.length || 0;
    const answers = skipQuestion
      ? (row.answers || [])
          .map((answerId) => {
            return skipQuestion.answers.find((answer) => answer.id === answerId);
          })
          .filter((answer) => !!answer)
      : [];
    if (!skipQuestion || answers.length === 0 || answers.length !== initialAnswersLength) {
      hasQuestionLogicError = true;
    }

    return {
      answers,
      operator: QUESTION_LOGIC_OPERATORS[row.operator],
      question: skipQuestion,
    };
  };

  if (isMultiLevelDisqualifyEnabled) {
    for (const questionId in modifiedQuestionLogic) {
      const disqualify = modifiedQuestionLogic[questionId]?.disqualify;
      if (disqualify && !Array.isArray(disqualify.logic) && Array.isArray(disqualify.answers)) {
        const legacyOperator = disqualify.operator || 'ANY';
        disqualify.logic = [
          {
            answers: disqualify.answers,
            operator: legacyOperator,
          },
        ];
        disqualify.operator = 'ANY';
        delete disqualify.answers;
      }
    }
  } else {
    for (const questionId in modifiedQuestionLogic) {
      const disqualify = modifiedQuestionLogic[questionId]?.disqualify;
      if (disqualify && Array.isArray(disqualify.logic) && !Array.isArray(disqualify.answers)) {
        const answerRow = disqualify.logic.find((row) => Array.isArray(row.answers));
        if (!answerRow) hasQuestionLogicError = true;
        disqualify.answers = answerRow?.answers || [];
        disqualify.operator = answerRow?.operator || 'ANY';
        delete disqualify.logic;
      }
    }
  }

  for (const questionId in modifiedQuestionLogic) {
    // Question group logic is keyed by group id (not question id). Hydrate every branch of
    // each group, mirroring per-question skip logic, then skip the per-question branches.
    // Group disqualify rows are skip-shaped, so they hydrate the same way.
    if (questionId === QUESTION_GROUP_LOGIC) {
      const groupLogic = modifiedQuestionLogic[questionId];
      for (const groupId in groupLogic) {
        const group = groupLogic[groupId];
        QUESTION_GROUP_LOGIC_BRANCHES.forEach((branch) => {
          const branchLogic = group?.[branch];
          if (!branchLogic) return;
          branchLogic.operator = QUESTION_LOGIC_OPERATORS[branchLogic.operator];
          branchLogic.logic = branchLogic.logic.map(hydrateSkipLogicRow);
        });
      }
      continue;
    }

    const question = modifiedQuestionLogic[questionId];
    if (question.disqualify) {
      const disqualifyQuestion = findQuestionLogicQuestionById(questionLogicQuestions, questionId);

      if (!isMultiLevelDisqualifyEnabled && Array.isArray(question.disqualify.answers)) {
        question.disqualify.operator = QUESTION_LOGIC_OPERATORS[question.disqualify.operator];
        const initialAnswersLength = question.disqualify.answers.length;
        const answers = disqualifyQuestion
          ? question.disqualify.answers
              .map((answerId) => {
                return disqualifyQuestion.answers.find((answer) => answer.id === answerId);
              })
              .filter((answer) => !!answer)
          : [];
        if (
          !disqualifyQuestion ||
          answers.length === 0 ||
          answers.length !== initialAnswersLength
        ) {
          hasQuestionLogicError = true;
        }
        question.disqualify.answers = answers;
        question.disqualify.hasAnswerError = false;
        question.disqualify.isFirstAnswerError = false;
      } else {
        if (
          !Array.isArray(question.disqualify.logic) &&
          Array.isArray(question.disqualify.answers)
        ) {
          const legacyOperator = question.disqualify.operator || 'ANY';
          question.disqualify.logic = [
            {
              answers: question.disqualify.answers,
              operator: legacyOperator,
            },
          ];
          question.disqualify.operator = 'ANY';
          delete question.disqualify.answers;
        }

        question.disqualify.operator = QUESTION_LOGIC_OPERATORS[question.disqualify.operator];
        question.disqualify.logic = (question.disqualify.logic || []).map((row) => {
          if (row.url_variable === instantSurveyGroupRefUrlVariable) {
            if ((row.values || []).length === 0) {
              hasQuestionLogicError = true;
            }
            return {
              hasAnswerError: false,
              operator: QUESTION_LOGIC_OPERATORS[row.operator],
              url_variable: instantSurveyGroupRefUrlVariable,
              values: row.values,
            };
          }
          const initialAnswersLength = row.answers.length;
          const answers = disqualifyQuestion
            ? row.answers
                .map((answerId) => {
                  return disqualifyQuestion.answers.find((answer) => answer.id === answerId);
                })
                .filter((answer) => !!answer)
            : [];
          if (
            !disqualifyQuestion ||
            answers.length === 0 ||
            answers.length !== initialAnswersLength
          ) {
            hasQuestionLogicError = true;
          }
          return {
            answers,
            hasAnswerError: false,
            isFirstAnswerError: false,
            operator: QUESTION_LOGIC_OPERATORS[row.operator],
          };
        });
      }
    }
    if (question.skip) {
      question.skip.operator = QUESTION_LOGIC_OPERATORS[question.skip.operator];
      question.skip.logic = question.skip.logic.map(hydrateSkipLogicRow);
    }

    if (question.option) {
      for (const answerId in question.option) {
        question.option[answerId].operator =
          QUESTION_LOGIC_OPERATORS[question.option[answerId].operator];
        question.option[answerId].logic = question.option[answerId].logic.map((row) => {
          if (row.url_variable === instantSurveyGroupRefUrlVariable) {
            if (row.values.length === 0) {
              hasQuestionLogicError = true;
            }
            return {
              operator: QUESTION_LOGIC_OPERATORS[row.operator],
              url_variable: instantSurveyGroupRefUrlVariable,
              values: row.values,
            };
          } else if (row.comparison_value !== undefined) {
            const optionQuestion = findQuestionLogicQuestionById(
              questionLogicQuestions,
              row.question
            );

            if (!optionQuestion || row.comparison_value == null || row.comparison_value === '') {
              hasQuestionLogicError = true;
            }

            return {
              comparison_value: row.comparison_value,
              operator: QUESTION_LOGIC_OPERATORS[row.operator],
              question: optionQuestion,
            };
          } else {
            const optionQuestion = findQuestionLogicQuestionById(
              questionLogicQuestions,
              row.question
            );
            const initialAnswersLength = row.answers?.length || 0;
            const answers = optionQuestion
              ? row.answers
                  .map((answerId) => {
                    return optionQuestion.answers.find((answer) => answer.id === answerId);
                  })
                  .filter((answer) => !!answer)
              : [];
            if (
              !optionQuestion ||
              answers.length === 0 ||
              answers.length !== initialAnswersLength
            ) {
              hasQuestionLogicError = true;
            }
            return {
              answers,
              operator: QUESTION_LOGIC_OPERATORS[row.operator],
              question: optionQuestion,
            };
          }
        });
      }
    }
  }
  return { hasQuestionLogicError, questionLogic: modifiedQuestionLogic };
};
